import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Heart, MessageCircle, Share2, ArrowRight, Image, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 获取图片URL
const getImageUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&q=80';
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

// 格式化时间
const formatTime = (dateStr, t) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = (now - date) / 1000;
  
  if (diff < 60) return t('notifications.justNow', '刚刚');
  if (diff < 3600) return t('notifications.minutesAgo', '{{count}}分钟前', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('notifications.hoursAgo', '{{count}}小时前', { count: Math.floor(diff / 3600) });
  if (diff < 604800) return t('notifications.daysAgo', '{{count}}天前', { count: Math.floor(diff / 86400) });
  return date.toLocaleDateString();
};

// 帖子类型图标
const PostTypeIcon = ({ type }) => {
  switch (type) {
    case 'artwork_share':
      return <Image className="w-3 h-3" />;
    default:
      return <FileText className="w-3 h-3" />;
  }
};

// 单个帖子卡片 - 统一样式，与作品卡片一致
const PostCard = ({ post, t }) => {
  const hasImage = post.images?.length > 0 || post.artwork_image;
  const mainImage = post.artwork_image || post.images?.[0];
  
  // 判断是否是作品分享帖子
  const isArtworkShare = post.post_type === 'artwork_share' && post.artwork_id;
  
  // 根据帖子类型决定链接地址
  const postLink = isArtworkShare 
    ? `/artwork/${post.artwork_id}` 
    : `/community/post/${post.id}`;
  
  // 根据帖子类型决定显示内容
  // artwork_share 类型直接显示作品标题，不显示"发布了新作品"
  const displayContent = isArtworkShare 
    ? post.artwork_title 
    : (post.content || post.artwork_title);
  
  // 显示作品描述（如果有）
  const displayDescription = isArtworkShare && post.artwork_description
    ? post.artwork_description
    : null;
  
  return (
    <Link 
      to={postLink}
      className="group block"
      data-testid={`latest-post-${post.id}`}
    >
      {/* 卡片容器 - 统一白色背景、圆角、阴影 */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300">
        {/* 图片区域 */}
        {hasImage && (
          <div className="aspect-[4/3] overflow-hidden relative">
            <img
              src={getImageUrl(mainImage)}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        )}
        
        {/* 内容区域 */}
        <div className="p-4">
          {/* 用户信息 */}
          <div className="flex items-center gap-2 mb-2">
            <Avatar className="w-7 h-7">
              <AvatarImage src={getImageUrl(post.user_avatar)} />
              <AvatarFallback className="bg-amber-100 text-amber-700 text-xs">
                {post.user_name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900 truncate">{post.user_name}</p>
            </div>
            <span className="text-xs text-amber-500">{formatTime(post.created_at, t)}</span>
          </div>
          
          {/* 帖子内容 - artwork_share直接显示作品标题 */}
          <p className="text-sm text-amber-700/80 line-clamp-2 mb-1 font-medium">
            {displayContent}
          </p>
          
          {/* 作品描述（仅artwork_share类型） */}
          {displayDescription && (
            <p className="text-xs text-amber-600/60 line-clamp-1 mb-2">
              {displayDescription}
            </p>
          )}
          
          {/* 互动数据 */}
          <div className="flex items-center gap-4 text-amber-500 text-xs mt-2">
            <span className="flex items-center gap-1">
              <Heart className="w-3.5 h-3.5" />
              {post.likes_count || 0}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="w-3.5 h-3.5" />
              {post.comments_count || 0}
            </span>
            <span className="flex items-center gap-1">
              <Share2 className="w-3.5 h-3.5" />
              {post.shares_count || 0}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const LatestPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/posts?limit=6`);
        setPosts(res.data || []);
      } catch (err) {
        console.error('Failed to fetch posts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-amber-50/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse shadow-sm">
                <div className="aspect-[4/3] bg-amber-100" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-amber-100 rounded w-3/4" />
                  <div className="h-3 bg-amber-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <section id="latest-posts" className="py-16 bg-amber-50/50" data-testid="latest-posts-section">
      <div className="container mx-auto px-4">
        {/* 标题区域 - 统一居中样式 */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-light text-amber-900 mb-2">
            {t('home.latestPosts', '最新动态')}
          </h2>
          <p className="text-amber-700/70 text-sm">
            {t('home.latestPostsDesc', '来自生态社区的精彩分享')}
          </p>
        </div>

        {/* 帖子网格 - 改为4列 */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {posts.slice(0, 4).map((post) => (
            <PostCard 
              key={post.id} 
              post={post} 
              t={t}
            />
          ))}
        </div>

        {/* 查看全部按钮 */}
        <div className="text-center mt-10">
          <Link 
            to="/community"
            className="inline-flex items-center text-amber-700 hover:text-amber-900 px-6 py-2 rounded-full border border-amber-300 hover:border-amber-400 transition-colors"
            data-testid="view-all-posts-btn"
          >
            {t('home.viewAllPosts', '查看全部')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestPosts;
